// src/features/fb/cookies/playbook/CookiesPlayBook.jsx
// Cookies-specific PlayBook configuration component

import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import PlayBookSection from "../../../../shared/sections/PlayBookSection";
import { apiFetch } from "../../../../api/apiClient";
import {
  getPlaybookSliderRanges,
  getPrimaryPackagingOptions,
} from "../../../../utils/commodityConfig";

/**
 * CookiesPlayBook - Cookies commodity-specific playbook configuration
 * Wraps PlayBookSection with Cookies-specific options and defaults
 */
const mergePrimaryPackagingOptions = (options, selectedValue) => {
  const merged = [];
  const seen = new Set();

  const addOption = (value) => {
    const normalized = String(value || "").trim();
    if (!normalized) return;
    const key = normalized.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(normalized);
  };

  addOption(selectedValue);
  (Array.isArray(options) ? options : []).forEach(addOption);
  return merged;
};

export default function CookiesPlayBook({ data, onChange, tower = "FB", commodity = "Cookies" }) {
  const exactCostingScope = tower.toUpperCase() === "FB" && ["COOKIES", "ICECREAMS"].includes(commodity.replace(/[\s_-]/g, "").toUpperCase());
  const selectedPrimaryPackaging = data?.primary_packaging || data?.primaryPackaging || "";
  const fallbackPrimaryPackagingOptions = getPrimaryPackagingOptions(commodity);
  const [primaryPackagingOptions, setPrimaryPackagingOptions] = useState(() =>
    mergePrimaryPackagingOptions(fallbackPrimaryPackagingOptions, selectedPrimaryPackaging),
  );

  const secondaryPackagingOptions = [
    "Carton Box",
    "Display Box",
    "Shrink Wrap Bundle",
    "Tray Pack",
    "Master Carton",
  ];

  const tertiaryPackagingOptions = [
    "Pallet",
    "Shipping Container",
    "Corrugated Box",
    "Wooden Crate",
  ];

  useEffect(() => {
    setPrimaryPackagingOptions((prev) =>
      mergePrimaryPackagingOptions(prev, selectedPrimaryPackaging),
    );
  }, [selectedPrimaryPackaging]);

  useEffect(() => {
    let isMounted = true;

    const fetchPackagingOptions = async () => {
      try {
        const params = new URLSearchParams({ tower, commodity });
        const response = await apiFetch(`/api/fb/generic/packaging-options?${params.toString()}`);

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        const options = Array.isArray(result?.options) ? result.options : [];

        if (isMounted) {
          setPrimaryPackagingOptions((prev) =>
            mergePrimaryPackagingOptions(
              [...fallbackPrimaryPackagingOptions, ...(options.length > 0 ? options : prev)],
              selectedPrimaryPackaging,
            ),
          );
        }
      } catch (error) {
        console.error("Failed to load primary packaging options:", error);
      }
    };

    fetchPackagingOptions();

    return () => {
      isMounted = false;
    };
  }, [tower, commodity, selectedPrimaryPackaging, fallbackPrimaryPackagingOptions]);

  // Cookies-specific storage and regulatory options
  const storageConditionsOptions = [
    "Room Temperature",
    "Cool & Dry",
    "Climate Controlled",
    "Humidity Controlled",
  ];

  const regulatoryOptions = [
    "FDA",
    "USDA",
    "HACCP",
    "GMP",
    "ISO 22000",
    "FSSAI",
    "EU Food Safety",
    "Kosher",
    "Halal",
  ];

  const ranges = useMemo(
    () => {
      const configured = getPlaybookSliderRanges(commodity, tower, data);
      return exactCostingScope ? { ...configured, moisture: { ...configured.moisture, min: 0 }, process: { ...configured.process, min: 0 }, packaging: { ...configured.packaging, min: 0 } } : configured;
    },
    [commodity, tower, data, exactCostingScope],
  );

  return (
    <Box>
      <PlayBookSection
        data={data}
        onChange={onChange}
        ranges={ranges}
        primaryPackagingOptions={primaryPackagingOptions}
        secondaryPackagingOptions={secondaryPackagingOptions}
        tertiaryPackagingOptions={tertiaryPackagingOptions}
        storageConditionsOptions={storageConditionsOptions}
        regulatoryOptions={regulatoryOptions}
        readOnlyOhp={exactCostingScope}
      />
    </Box>
  );
}
